import { motion } from "framer-motion";
import { Clock, MapPin } from "lucide-react";

export interface Artist {
  name: string;
  role?: string;
  image?: string;
  imageMobile?: string;
  logoUrl?: string;
  accentColor?: string;
  revealed: boolean;
  /** Set time, e.g. "9:30 – 11:00 PM" */
  time?: string;
  /** Stage / room, e.g. "Main Stage" or "The Deck" */
  stage?: string;
  /** "Friday" | "Saturday" — used in the detail sheet */
  day?: string;
  songLabel?: string;
  songUrl?: string;
  bio?: string;
  topSongs?: string[];
  spotifyUrl?: string;
}

interface ArtistCardProps {
  artist: Artist;
  index: number;
  /** Opens the shared detail sheet (What-to-Wear style). */
  onOpen?: (artist: Artist) => void;
  /** Small grid tiles (e.g. the Ambrosia bill): hide the set time on the card
   *  — it stays in the detail sheet. Keeps the small cards clean. */
  compact?: boolean;
}

const TBACard = ({ index }: { index: number }) => {
  return (
    <motion.div
      className="relative aspect-square rounded-2xl overflow-hidden"
      initial={{ opacity: 0, y: 40 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.6, delay: index * 0.1 }}
    >
      {/* Dark base */}
      <div className="absolute inset-0 bg-yacht-charcoal" />

      {/* Animated gradient orbs */}
      <motion.div
        className="absolute inset-0 opacity-40"
        style={{
          background:
            "radial-gradient(circle at 30% 40%, hsl(var(--yacht-blue)) 0%, transparent 50%), radial-gradient(circle at 70% 60%, hsl(var(--yacht-teal)) 0%, transparent 50%), radial-gradient(circle at 50% 80%, hsl(var(--yacht-deepteal)) 0%, transparent 60%)",
        }}
        animate={{
          backgroundPosition: ["0% 0%", "100% 100%", "0% 0%"],
        }}
        transition={{ duration: 8, repeat: Infinity, ease: "linear" }}
      />

      {/* Blurred silhouette */}
      <div className="absolute inset-0 flex items-center justify-center">
        <div
          className="w-[55%] h-[70%] rounded-full relative"
          style={{
            background: "radial-gradient(ellipse at 50% 35%, rgba(255,255,255,0.08) 0%, transparent 70%)",
            filter: "blur(12px)",
          }}
        >
          <div
            className="absolute top-[8%] left-1/2 -translate-x-1/2 w-[40%] aspect-square rounded-full"
            style={{ background: "rgba(255,255,255,0.06)", filter: "blur(8px)" }}
          />
          <div
            className="absolute top-[35%] left-1/2 -translate-x-1/2 w-[90%] h-[50%] rounded-t-full"
            style={{ background: "rgba(255,255,255,0.04)", filter: "blur(10px)" }}
          />
        </div>
      </div>

      {/* Gradient animated question mark */}
      <div className="absolute inset-0 flex items-center justify-center z-10">
        <motion.span
          className="text-[20vw] md:text-[8vw] lg:text-[6vw] font-bold select-none"
          style={{
            background: "linear-gradient(135deg, hsl(var(--yacht-yellow)), hsl(var(--yacht-blue)), hsl(var(--yacht-yellow)))",
            backgroundSize: "200% 200%",
            WebkitBackgroundClip: "text",
            WebkitTextFillColor: "transparent",
            filter: "drop-shadow(0 0 30px rgba(242, 220, 5, 0.3))",
          }}
          animate={{ backgroundPosition: ["0% 0%", "100% 100%", "0% 0%"] }}
          transition={{ duration: 4, repeat: Infinity, ease: "linear" }}
        >
          ?
        </motion.span>
      </div>

      {/* Noise texture */}
      <div
        className="absolute inset-0 opacity-[0.15] pointer-events-none mix-blend-overlay"
        style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 400 400' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noiseFilter'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noiseFilter)'/%3E%3C/svg%3E")`,
        }}
      />

      {/* Label */}
      <div className="absolute bottom-0 left-0 right-0 p-5 z-10">
        <span className="text-white/40 text-xs font-semibold tracking-[0.3em] uppercase">
          Coming Soon
        </span>
      </div>
    </motion.div>
  );
};

const ArtistCard = ({ artist, index, onOpen, compact = false }: ArtistCardProps) => {
  if (!artist.revealed) {
    return <TBACard index={index} />;
  }

  const open = () => onOpen?.(artist);
  const hasDetail = Boolean(artist.bio || artist.topSongs);

  return (
    <motion.div
      className="relative rounded-2xl overflow-hidden group w-full cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-yacht-yellow"
      initial={{ opacity: 0, y: 40 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true }}
      transition={{ duration: 0.6, delay: index * 0.1 }}
      onClick={open}
      role="button"
      tabIndex={0}
      aria-label={`${artist.name}${artist.role ? ` — ${artist.role}` : ""}. Tap for details.`}
      onKeyDown={(e) => {
        if (e.key === "Enter" || e.key === " ") {
          e.preventDefault();
          open();
        }
      }}
    >
      <div className="relative aspect-square bg-yacht-charcoal overflow-hidden">
        {artist.image && (
          artist.imageMobile ? (
            <picture>
              <source media="(max-width: 640px)" srcSet={artist.imageMobile} />
              <img
                src={artist.image}
                alt={artist.name}
                className="w-full h-full object-cover transition-all duration-700 group-hover:scale-[1.03]"
                style={artist.logoUrl ? { objectPosition: "center 15%" } : undefined}
              />
            </picture>
          ) : (
            <img
              src={artist.image}
              alt={artist.name}
              className="w-full h-full object-cover transition-all duration-700 group-hover:scale-[1.03]"
              style={artist.logoUrl ? { objectPosition: "center 15%" } : undefined}
            />
          )
        )}

        <div className="absolute inset-0 bg-gradient-to-t from-yacht-charcoal via-yacht-charcoal/20 to-transparent" />

        {/* Stage pill — top left */}
        {artist.stage && (
          <span className="absolute top-3 left-3 z-20 flex items-center gap-1 rounded-full bg-black/45 backdrop-blur-md px-2.5 py-1 text-[11px] font-semibold uppercase tracking-[0.08em] text-white">
            <MapPin className="w-3 h-3 text-yacht-yellow" strokeWidth={2.25} />
            {artist.stage}
          </span>
        )}

        {artist.logoUrl && (
          <img
            src={artist.logoUrl}
            alt=""
            aria-hidden="true"
            className="absolute left-1/2 bottom-[26%] -translate-x-1/2 w-[58%] max-w-[180px] z-10 pointer-events-none"
            style={{
              filter: `drop-shadow(0 4px 16px ${artist.accentColor || "#f2dc05"}88) drop-shadow(0 2px 8px rgba(0,0,0,0.8))`,
            }}
          />
        )}

        <div className="absolute bottom-0 left-0 right-0 p-5 md:p-6 z-10">
          {artist.logoUrl ? (
            <h3 className="sr-only">{artist.name}</h3>
          ) : (
            <h3 className="text-white font-bold text-2xl md:text-3xl leading-tight">{artist.name}</h3>
          )}
          {artist.role && <p className="text-white/60 text-sm md:text-base mt-1 line-clamp-2">{artist.role}</p>}

          {/* Set time — hidden on compact (small) tiles; still shown in the sheet */}
          {artist.time && !compact && (
            <p className="text-yacht-yellow text-sm font-semibold mt-2.5 flex items-center gap-1.5">
              <Clock className="w-3.5 h-3.5" strokeWidth={2.25} />
              {artist.time}
            </p>
          )}
        </div>

        {/* Tap-for-details affordance */}
        {hasDetail && (
          <span className="absolute bottom-4 right-4 z-10 flex items-center gap-1 text-white/70 text-xs font-medium transition-colors group-hover:text-white">
            Details
            <span className="text-yacht-yellow text-sm leading-none">›</span>
          </span>
        )}
      </div>
    </motion.div>
  );
};

export default ArtistCard;
